// @ts-nocheck
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";

import translate from "@insite/client-framework/Translate";
import { WishListLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import { Clickable, GridContainer, GridItem, Hidden, Icon, Typography, VisuallyHidden } from "@insite/mobius/index";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import { productLineProps } from "@store/Themes/common/props/productLineProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Trash2Fill from "@toolkit/Library/Components/Icons/Trash2/Trash2Fill";
import React from "react";
import { css } from "styled-components";

interface MyListDetailsRestrictedLineProps {
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    wishListLine: WishListLineModel;
    canEditWishList: boolean;
    wishListLineIsRestricted: boolean;
    wishListLineIsDiscontinued: boolean;
}

interface MyListDetailsRestrictedLineStyles {
    lineInnerContainer?: GridContainerProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    partNumbersHidden?: HiddenProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    priceAndAvailabilityGridItem?: GridItemProps;
    restrictedMessageText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    actionDeleteButton?: ButtonPresentationProps;
    trashIcon?: IconProps;
    partNumbersGridItem?: GridItemProps;
}

const styles: MyListDetailsRestrictedLineStyles = {
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                justify-content: "flex-start";
            `,
        },
        container: {
            css: css`
                flex-grow: 0;
            `,
        },
    },
    actionDeleteButton: {
        color: "danger.light",
        css: css`
            ${({ theme }) => theme?.button?.defaultProps?.css}
            margin-left: ${pxToRem(5)};
            min-width: 0;
            padding: ${pxToRem(0, 5)};
        `,
    },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            min-width: 36px
            align-items: center;
        `,
    },
    restrictedMessageText: { color: "warning" },

    trashIcon: {
        src: Trash2Fill,
        color: "danger",
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding-left: 0;
        `,
    },
    lineInnerContainer: {
        gap: 20,
        offsetCss: css`
            display: flex;
            flex-wrap: nowrap !important;

            ${breakpointValues(
                ["flex-start !important", null, null, "center !important", "center !important"],
                ["align-items"],
            )}
        `,
    },
};

const MyListDetailsRestrictedLine: React.FC<MyListDetailsRestrictedLineProps> = ({
    canEditWishList,
    wishListLineIsDiscontinued,
    wishListLine,
    wishListLineIsRestricted,
    onDeleteClick,
}) => {
    const deleteLabel = translate("Delete");
    const { product } = useProductContext();

    return (
        <GridContainer
            {...styles.lineInnerContainer}
            data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
        >
            <GridItem {...styles.restrictedDescriptionGridItem}>
                <Typography {...styles.restrictedDescriptionText}>{wishListLine.shortDescription}</Typography>
                <Hidden {...styles.partNumbersHidden}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                </Hidden>
            </GridItem>
            <GridItem {...styles.partNumbersGridItem}>
                <ProductPartNumbers
                    productNumber={product.productNumber}
                    customerProductNumber={product.customerProductNumber}
                    manufacturerItem={product.manufacturerItem}
                    extendedStyles={styles.productPartNumbersStyles}
                />
            </GridItem>
            <GridItem {...styles.priceAndAvailabilityGridItem}>
                <Typography {...styles.restrictedMessageText} data-test-selector="restrictedMessage">
                    {wishListLineIsRestricted && <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>}
                    {wishListLineIsDiscontinued && <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}</>}
                </Typography>
            </GridItem>
            <GridItem {...styles.buttonsGridItem}>
                {canEditWishList && (
                    // Valtech Custom Code -->
                    <Clickable
                        {...styles.actionDeleteButton}
                        onClick={() => onDeleteClick(wishListLine)}
                        data-test-selector="removeRestricted"
                    >
                        <Icon {...styles.trashIcon} />
                        <VisuallyHidden>{deleteLabel}</VisuallyHidden>
                    </Clickable>
                    // Valtech Custom Code <--
                )}
            </GridItem>
        </GridContainer>
    );
};

export default MyListDetailsRestrictedLine;
